import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { payrollConfigApi, type PayrollConfigDto } from '../../api/payrollConfig';
import { useIsMirror } from '../../hooks/useIsMirror';
import { useReportScreen } from '../../components/ScreenContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import AccountSelect from '../../components/AccountSelect';

/**
 * P1d — DUŽ tarifai ir sąskaitos: the freeze source for wage-sheet generation.
 * Rates are DATA (an edit adds a new effective-dated row — history stays);
 * every generated sheet freezes its own snapshot, so later edits never
 * touch existing sheets. Mirror workspaces: read-only (ADR-004).
 */
export default function PayrollConfigPage() {
  const isMirror = useIsMirror();
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['payroll-config'],
    queryFn: payrollConfigApi.get,
  });

  const [rateEdits, setRateEdits] = useState<Record<number, string>>({});
  const [accountEdits, setAccountEdits] = useState<Record<number, number | null>>({});
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (saved) {
      const t = setTimeout(() => setSaved(false), 1500);
      return () => clearTimeout(t);
    }
  }, [saved]);

  const rateValue = (r: { rateTypeCode: number; value: string }) =>
    rateEdits[r.rateTypeCode] ?? r.value;
  const accountValue = (a: { accountType: number; accountNumber: number | null }) =>
    (a.accountType in accountEdits ? accountEdits[a.accountType] : a.accountNumber) ?? null;

  const dirty =
    Object.keys(rateEdits).length > 0 || Object.keys(accountEdits).length > 0;

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      if (Object.keys(rateEdits).length > 0) {
        await payrollConfigApi.saveRates(
          Object.entries(rateEdits).map(([code, value]) => ({
            rateTypeCode: Number(code),
            value,
          })),
        );
      }
      const accountUpdates = Object.entries(accountEdits)
        .filter(([, v]) => v != null)
        .map(([type, v]) => ({ accountType: Number(type), accountNumber: v as number }));
      if (accountUpdates.length > 0) await payrollConfigApi.saveAccounts(accountUpdates);
      setRateEdits({});
      setAccountEdits({});
      setSaved(true);
      await queryClient.invalidateQueries({ queryKey: ['payroll-config'] });
    } catch (e: any) {
      setError(e.message || 'Nepavyko išsaugoti');
    } finally {
      setSaving(false);
    }
  };

  useReportScreen(`DUŽ tarifai ir sąskaitos${isMirror ? ' (tik skaityti)' : ''}`, {
    recordCount: data?.rates.length ?? 0,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">DUŽ tarifai ir sąskaitos</h1>
          <p className="text-sm text-gray-500">
            Žiniaraščių skaičiavimo šaltinis — sukurtas žiniaraštis įšaldo savo tarifų kopiją.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/payroll"
            className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:border-gray-400"
          >
            ← Darbo užmokestis
          </Link>
          {!isMirror && (
            <button
              onClick={save}
              disabled={saving || !dirty}
              className="px-4 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40"
            >
              {saving ? '…' : saved ? '✓' : 'Išsaugoti'}
            </button>
          )}
        </div>
      </div>

      {isMirror && (
        <p className="text-sm text-gray-500">Ši darbo sritis tik skaityti — tarifus keiskite Apskaita5 programoje.</p>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {isLoading ? (
        <LoadingSpinner />
      ) : isError || !data ? (
        <div className="error-message">Nepavyko įkelti tarifų — atnaujinkite puslapį.</div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          <RateTable
            data={data}
            disabled={isMirror}
            rateValue={rateValue}
            onRateChange={(code, value) => {
              setRateEdits(prev => ({ ...prev, [code]: value }));
              setSaved(false);
            }}
          />
          <AccountTable
            data={data}
            disabled={isMirror}
            accountValue={accountValue}
            onAccountChange={(type, value) => {
              setAccountEdits(prev => ({ ...prev, [type]: value }));
              setSaved(false);
            }}
          />
        </div>
      )}
    </div>
  );
}

function RateTable({
  data,
  disabled,
  rateValue,
  onRateChange,
}: {
  data: PayrollConfigDto;
  disabled: boolean;
  rateValue: (r: { rateTypeCode: number; value: string }) => string;
  onRateChange: (code: number, value: string) => void;
}) {
  return (
    <div className="card">
      <h2 className="text-sm font-semibold text-gray-700 mb-2">Tarifai</h2>
      <table className="data-table">
        <thead>
          <tr>
            <th>Tarifas</th>
            <th>Reikšmė</th>
            <th>Galioja nuo</th>
          </tr>
        </thead>
        <tbody>
          {data.rates.map(r => (
            <tr key={r.rateTypeCode}>
              <td>{r.rateTypeName}</td>
              <td>
                <input
                  style={{ width: r.rateTypeCode === 100 ? '16rem' : '6rem' }}
                  value={rateValue(r)}
                  disabled={disabled}
                  onChange={e => onRateChange(r.rateTypeCode, e.target.value)}
                />
              </td>
              <td className="text-xs text-gray-400">{r.validFrom.slice(0, 10)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AccountTable({
  data,
  disabled,
  accountValue,
  onAccountChange,
}: {
  data: PayrollConfigDto;
  disabled: boolean;
  accountValue: (a: { accountType: number; accountNumber: number | null }) => number | null;
  onAccountChange: (type: number, value: number | null) => void;
}) {
  return (
    <div className="card">
      <h2 className="text-sm font-semibold text-gray-700 mb-2">DUŽ sąskaitos</h2>
      <table className="data-table">
        <thead>
          <tr>
            <th>Paskirtis</th>
            <th>Sąskaita</th>
          </tr>
        </thead>
        <tbody>
          {data.accounts.map(a => (
            <tr key={a.accountType}>
              <td>{a.accountTypeName}</td>
              <td>
                <AccountSelect
                  value={accountValue(a)}
                  onChange={v => onAccountChange(a.accountType, v)}
                  className={disabled ? 'opacity-60' : undefined}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
